"use client";

import styles from "./RootLayout.module.scss";
import { FC, memo, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { classNames } from "@/lib/utils";
import HeaderBase from "@/components/header/Header";
import { Stock } from "@/models/stock";
import { useAtom, useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { Box, Button, Card, Container, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { hideTickertapeState, userState, textColorState, themeState, lighterBackgroundColorState, darkerBackgroundColorState, stockReportAvailabilityState, tradingIdeaAvailabilityState } from "@/lib/store";
import StableErrorBoundary from "@/components/error-boundary/StableErrorBoundary";
import ErrorFallback from "@/components/error-fallback/ErrorFallback";
import ContentLayout from "../content/ContentLayout";
import StockHeaderInfo from "@/components/stock-header-info/StockHeaderInfo";
import StockLinks from "@/components/stock-links/StockLinks";
import StockReportBadge from "@/components/stock-report-badge/StockReportBadge";
import NextCorporateActionChip from "@/components/next-corporate-action-chip/NextCorporateActionChip";
import SettingsIcon from "@mui/icons-material/Settings";
import NewTickerTape from "@/components/new-ticker-tape/NewTickerTape";
import StockHeaderLogo from "@/components/stock-header-logo/StockHeaderLogo";
import { useSetAtom } from "jotai";
import { getStockReportExists } from "@/controllers/market-intelligence";
import { getTradingIdeaExists } from "@/controllers/trading-ideas";
import TradingIdeaBadge from "@/components/trading-idea-badge/TradingIdeaBadge";
import { useIdleEffect } from "@/lib/defer";
import AboutThisData from "@/components/locked-value-panel/AboutThisData";
import LockedValuePanel from "@/components/locked-value-panel/LockedValuePanel";
import StockKeyData from "@/components/stock-key-data/StockKeyData";
import SiteFooter from "@/components/site-footer/SiteFooter";
import { StockNewsItem } from "@/models/stock-news";
import StockLatestNews from "@/components/stock-latest-news/StockLatestNews";
import DeferredMount, { DeferHiddenBlocksContext } from "@/components/deferred-mount/DeferredMount";
import { stockHeading, stockTabOf } from "@/lib/stock-seo";
import type { ShortList } from "@/lib/short-lists";
import { ShortListBlock, ShortListView } from "@/components/short-list-table/ShortListTable";
import dynamic from "next/dynamic";

// Inside the settings drawer, which renders its content only while open: its code loads on first open.
const TickertapeSettings = dynamic(() => import("@/components/tickertape-settings/TickertapeSettings"), { ssr: false });

interface PropsTypes {
    children?: ReactNode;
    stock?: Stock;
    // Stock overview and short-interest tab only (their getServerSideProps); see StockLatestNews.
    latestNews?: StockNewsItem[] | null;
    // Stock pages only (stockPageGetServerSideProps): false for a browser's render, whose hidden and
    // below-the-fold blocks are drawn in the browser (DeferredMount); true for a crawler's full render.
    fullRender?: boolean;
    // /short-interest and /short-interest/short-squeeze (getStaticProps): the server-rendered list below the page.
    shortList?: ShortList | null;
    shortListView?: ShortListView;
}

// Memoized: its props change only with the stock and the scroll position, and it re-renders for its own atoms.
const Header = memo(HeaderBase);

/** Pages that carry the below-the-page key data and latest ortex.news headlines. */
const STOCK_SUMMARY_PAGES = ["/s/[exchange]/[ticker]", "/s/[exchange]/[ticker]/short-interest"];

const RootLayout: FC<PropsTypes> = ({ children, stock, latestNews, fullRender, shortList, shortListView }) => {
    const mainRef = useRef(null);
    const isMobile = useMediaQuery("(max-width: 768px)");
    // Both false on the server and in the first client render, so each header variant below is in
    // the server's HTML (CSS shows the right one) and the other is dropped once the width is known.
    const isDesktopWidth = useMediaQuery("(min-width: 769px)");

    const [user] = useAtom(userState);
    const textColor = useAtomValue(textColorState);
    const theme = useAtomValue(themeState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);

    const { pathname, push, events } = useRouter();

    const [page, setPage] = useState("");
    const [scrolledDown, setScrolledDown] = useState(false);
    const [settingsModal, setSettingsModal] = useState(false);
    const [hideTickertape, setHideTickertape] = useAtom(hideTickertapeState);

    const anchorPosition = isMobile ? "bottom" : "right";
    const id = settingsModal ? "simple-popover" : undefined;

    const handleClose = () => {
        setSettingsModal(false);
    };

    // ── Stock Report availability ────────────────────────────────
    // Single shared fetch when a stock-page mounts (or the user
    // navigates between tickers). Result is published into the
    // ``stockReportAvailabilityState`` atom so both the StockLinks
    // tab strip (which inserts a "Stock Report" tab when one
    // exists) and the top-bar ``<StockReportBadge/>`` (which adds
    // the click-through pill next to the company name on mobile
    // and next to the market-hours indicator on desktop) read
    // from the same source. ``null`` while resolving so consumers
    // can distinguish "still loading" from "no report exists".
    //
    // The *probe* is deferred to the browser's first idle period
    // (``useIdleEffect``): at about a second it was one of the two slowest
    // requests on a cold stock page, and nothing the reader is looking at
    // waits on it - it adds a tab and a pill. Starting it after the page has
    // drawn keeps it out of the mount burst the chart's own data is in.
    //
    // Clearing the previous ticker's answer is NOT deferred. It has to happen
    // in the same commit as the navigation, or the old stock's tab and badge
    // stay on screen against the new stock's name for as long as the deferral
    // lasts.
    const setStockReportAvailability = useSetAtom(stockReportAvailabilityState);
    useEffect(() => {
        setStockReportAvailability(null);
    }, [stock?.ticker, stock?.exchangesymbol, setStockReportAvailability]);
    //
    // No AbortController, on purpose. `installGetDeduplication` refuses to
    // share any GET carrying a signal - joining them would let one caller's
    // abort cancel another's request - so passing one opted these probes out of
    // the very machinery that exists to collapse duplicate calls, and the page
    // issued this request twice. A plain `cancelled` flag ignores the late
    // answer just as effectively, and lets a second mount join the first
    // request instead of starting another. The response is a few bytes; not
    // aborting it costs nothing worth the duplicate.
    useIdleEffect(() => {
        if (!stock?.ticker || !stock?.exchangesymbol) return;
        const ticker = stock.ticker;
        const exchange = stock.exchangesymbol;
        let cancelled = false;
        getStockReportExists(ticker, undefined, exchange)
            .then((r) => {
                if (cancelled) return;
                setStockReportAvailability({
                    exists: !!r?.exists,
                    headline: r?.headline ?? null,
                    generatedAt: r?.generatedAt ?? null,
                    deepLinkPath: `/s/${exchange}/${ticker}/stock-report`,
                });
            })
            .catch(() => {
                // 404 / network errors collapse to "no report" — the
                // badge / tab simply don't appear. Never surface a
                // user-visible failure for the existence probe; the
                // rest of the stock page is unaffected.
                if (!cancelled) setStockReportAvailability({ exists: false });
            });
        return () => {
            cancelled = true;
        };
    }, [stock?.ticker, stock?.exchangesymbol, setStockReportAvailability]);

    // ── Trading Idea availability ────────────────────────────────
    // Same shape as above. The probe is gated server-side, so a user
    // without the product group gets exists=false and the tab and
    // callout simply never render — there is no client-side check to
    // forget, and no state in which an empty tab could appear.
    // Deferred, and cleared eagerly, for the same reasons as the report probe.
    const setTradingIdeaAvailability = useSetAtom(tradingIdeaAvailabilityState);
    useEffect(() => {
        setTradingIdeaAvailability(null);
    }, [stock?.ticker, stock?.exchangesymbol, setTradingIdeaAvailability]);
    // Same dedupe reasoning as the report probe above.
    useIdleEffect(() => {
        if (!stock?.ticker || !stock?.exchangesymbol) return;
        const ticker = stock.ticker;
        const exchange = stock.exchangesymbol;
        let cancelled = false;
        getTradingIdeaExists(ticker, undefined, exchange)
            .then((r) => {
                if (cancelled) return;
                setTradingIdeaAvailability({
                    exists: !!r?.exists,
                    id: r?.id,
                    headline: r?.headline ?? null,
                    direction: r?.direction,
                    publishedAt: r?.publishedAt ?? null,
                    deepLinkPath: `/s/${exchange}/${ticker}/trading-idea`,
                });
            })
            .catch(() => {
                // Fail closed, exactly like the report probe: any error
                // means no tab. Better to hide a real idea than to show
                // a tab we cannot stand behind.
                if (!cancelled) setTradingIdeaAvailability({ exists: false });
            });
        return () => {
            cancelled = true;
        };
    }, [stock?.ticker, stock?.exchangesymbol, setTradingIdeaAvailability]);

    useEffect(() => {
        if (pathname.includes("corporate-actions")) setPage("corporate-actions");
        else if (pathname.includes("commodity-exposure")) setPage("commodity-exposure");
        else if (pathname.includes("trading-signals")) setPage("trading-signals");
        else if (pathname.includes("short-interest")) setPage("short-interest");
        else if (pathname.includes("stock-scores")) setPage("stock-scores");
        else if (pathname.includes("fundamentals")) setPage("fundamentals");
        else if (pathname.includes("analysts")) setPage("analysts");
        else if (pathname.includes("insiders")) setPage("insiders");
        else if (pathname.includes("dividend")) setPage("dividend");
        else if (pathname.includes("holders")) setPage("holders");
        else if (pathname.includes("options")) setPage("options");
        else if (pathname.includes("events")) setPage("events");
        else if (pathname.includes("peers")) setPage("peers");
        else if (pathname.includes("eps")) setPage("eps");
        else if (pathname.includes("stocks-stamps")) setPage("stock-stamps");
        else if (pathname.endsWith("/bonds")) setPage("bonds");
        else if (pathname.endsWith("/stock-report")) setPage("stock-report");
        else setPage("overview");
    }, [pathname]);

    useEffect(() => {
        if (!user?.email) return;

        const tickertapeVisibility = user?.settings?.module_settings["tickerTape->tickertape-visibility"];

        if (tickertapeVisibility) {
            setHideTickertape(tickertapeVisibility.isHidden);
        } else {
            setHideTickertape(false);
        }
    }, [user]);

    const logError = (error: Error, info: { componentStack: string }) => {
        console.error(error, info);
    };

    /** Where each history entry was left, by Next's history `key`, so back and forward can return to it. */
    const scrollOffsets = useRef(new Map<string, number>());
    /** True between a navigation's start and its end, when the URL and the page on screen disagree. */
    const navigating = useRef(false);
    const historyKey = () => (window.history.state as { key?: string } | null)?.key;

    const handleScroll = () => {
        if (!mainRef.current) return;
        setScrolledDown(mainRef.current.scrollTop > 0 ? true : false);
        const key = historyKey();
        if (key && !navigating.current) scrollOffsets.current.set(key, mainRef.current.scrollTop);
    };
    useEffect(() => {
        if (mainRef.current) mainRef.current.addEventListener("scroll", handleScroll);

        return () => {
            if (mainRef.current) mainRef.current.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Pages scroll inside this container, not the window, so Next's scroll reset on navigation never
    // reaches it: a stock page opened from a scrolled screener kept the screener's offset and opened
    // with its tabs under the header. A new page starts at the top; a query-only change (a tab on the
    // same page) stays where it is; back and forward return to where that entry was left.
    useEffect(() => {
        const pathOf = (url: string) => url.split(/[?#]/)[0];
        let lastPath = window.location.pathname;
        // The history entry the page on screen belongs to. A push or replace starts before Next writes
        // its new entry, so the key still matches; back and forward start after the browser has moved,
        // so it doesn't. Hash-only steps and entries Next ignores never reach routeChangeStart, so they
        // refresh the key themselves and can't make a later navigation look like back/forward.
        let currentKey = historyKey();
        let isPop = false;
        const syncKey = () => {
            currentKey = historyKey();
        };
        const onPopState = () => {
            if (!(window.history.state as { __N?: boolean } | null)?.__N) syncKey();
        };
        const onStart = () => {
            navigating.current = true;
            const key = historyKey();
            isPop = !!key && key !== currentKey;
        };
        const onError = () => {
            navigating.current = false;
            isPop = false;
        };
        const onComplete = (url: string) => {
            navigating.current = false;
            const wasPop = isPop;
            isPop = false;
            syncKey();
            const root = mainRef.current;
            const path = pathOf(url);
            const pathChanged = path !== lastPath;
            lastPath = path;
            if (!root) return;
            if (wasPop) {
                const offset = currentKey ? scrollOffsets.current.get(currentKey) : undefined;
                // After the new page has painted, or the offset is clamped to a page that isn't tall yet.
                requestAnimationFrame(() => (root.scrollTop = offset ?? 0));
            } else if (pathChanged) {
                root.scrollTop = 0;
            }
        };
        window.addEventListener("popstate", onPopState);
        events.on("routeChangeStart", onStart);
        events.on("routeChangeComplete", onComplete);
        events.on("routeChangeError", onError);
        events.on("hashChangeComplete", syncKey);
        return () => {
            window.removeEventListener("popstate", onPopState);
            events.off("routeChangeStart", onStart);
            events.off("routeChangeComplete", onComplete);
            events.off("routeChangeError", onError);
            events.off("hashChangeComplete", syncKey);
        };
    }, [events]);

    const goToSettings = () => {
        push("/account");
        setSettingsModal(false);
    };

    // The server-rendered blocks below the page. Memoized, so this layout's own re-renders do not re-render them.
    const showSummary = !!stock && STOCK_SUMMARY_PAGES.includes(pathname);
    const belowPage = useMemo(
        () => (
            <>
                {showSummary && <StockKeyData stock={stock} shortData={stock.shortKeyData} />}
                {showSummary && <StockLatestNews stock={stock} items={latestNews} />}
                <SiteFooter stock={stock} />
            </>
        ),
        [showSummary, stock, latestNews]
    );

    // The page and, on stock pages, the stock header above it (name, session, tabs). Memoized, so a
    // re-render of this layout (the user, a color, the scroll position) does not re-render them.
    const heading = stock ? stockHeading(stock, stockTabOf(pathname)) : "";
    const pageBody = useMemo(
        () =>
            stock ? (
                <ContentLayout
                    title={
                        <Box>
                            {/* The header must not change height after the server render: the
                                whole page sits below it. So the phone/desktop split is made in
                                CSS (the server renders the desktop branch of every `isMobile`),
                                and on a phone the logo gets a row of its own, of fixed height,
                                which is also where the report and idea pills land when their
                                probes answer. They used to arrive under the subtitle and wrap
                                the title under the logo, pushing the page down ~50 px. */}
                            <Box className={styles.stockHeaderRow}>
                                <Box className={styles.stockHeaderLogoRow}>
                                    <StockHeaderLogo theme={theme} stock={stock} />
                                    {isMobile && (
                                        <Box className={styles.stockHeaderPills}>
                                            <StockReportBadge stock={stock} compact />
                                            <TradingIdeaBadge stock={stock} compact />
                                        </Box>
                                    )}
                                </Box>
                                <Box className={styles.stockHeaderTitle}>
                                    {/* The page's one H1, "Company (TICKER) Topic" for the tab shown (src/lib/stock-seo.ts). */}
                                    <Typography variant="h1" title={heading} sx={{ fontWeight: "bold", letterSpacing: "0.5px" }}>
                                        {heading}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: textColor }} id={styles["subtitle"]}>
                                        {`${stock.exchangesymbol} • ${stock.ticker} `} • {stock.isin}
                                    </Typography>
                                    {/* From the stock payload, so drawn by the server for phones too. */}
                                    {!isDesktopWidth && (
                                        <Box className={styles.phoneOnly}>
                                            <NextCorporateActionChip stock={stock} compact />
                                        </Box>
                                    )}
                                </Box>
                                {!isMobile && (
                                    <Box className={styles.desktopOnly}>
                                        <StockHeaderInfo stock={stock} />
                                    </Box>
                                )}
                                {/* Informational sibling of the price / session cluster —
                                    the upcoming ex-dividend or split from the stock base
                                    payload. Renders nothing when there is none. */}
                                {!isMobile && (
                                    <Box className={styles.desktopOnly}>
                                        <NextCorporateActionChip stock={stock} />
                                    </Box>
                                )}
                                {/* Desktop anchor: a growing flex spacer pushes the
                                    badge to the far right of the header row,
                                    separating it from the price / session cluster
                                    so it reads as a standalone call-to-action.
                                    Uses flexGrow + justifyContent: flex-end (rather
                                    than ml: auto) so the behaviour holds even when
                                    the row wraps. */}
                                {!isMobile && (
                                    <Box sx={{ flexGrow: 1, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
                                        <StockReportBadge stock={stock} />
                                        <TradingIdeaBadge stock={stock} />
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ height: "48px" }}>
                                {/* Hidden until the user has loaded (its own visibility rule), and on a
                                    phone the server's desktop strip is swapped for the menu at once:
                                    a browser render draws it just after hydration, as a transition. */}
                                <DeferredMount when="hydrated">
                                    <StockLinks stock={stock} />
                                </DeferredMount>
                            </Box>
                        </Box>
                    }
                    stockPage
                    // The peers tab grows with its list (every row and column visible) and scrolls as a page.
                    autoScroll={page === "peers"}
                >
                    <div className={classNames(styles[page], styles["stock-layout"])}>
                        <div className={styles.content}>
                            <Container className={styles.muiContainer} maxWidth={false}>
                                {children}
                            </Container>
                        </div>
                    </div>
                </ContentLayout>
            ) : (
                <Container className={styles.muiContainer} maxWidth={false}>
                    {children}
                </Container>
            ),
        [stock, heading, theme, textColor, isMobile, isDesktopWidth, page, children]
    );

    return (
        <DeferHiddenBlocksContext.Provider value={fullRender === false}>
            <div className="ion-app">
                <div className={styles.RootLayout} ref={mainRef}>
                    <Box className={styles.tickertapeBand}>
                        {!hideTickertape && <NewTickerTape />}
                        <Box className={styles.tickertapeSettingsBtn} sx={{ backgroundColor: theme === "dark" ? darkerBackgroundColor : lighterBackgroundColor }}>
                            <Tooltip title="Tickertape Settings" arrow>
                                <IconButton className={styles.button} onClick={() => setSettingsModal(true)} sx={{ p: 0 }}>
                                    <SettingsIcon style={{ color: textColor, fontSize: "18px" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    {/* Invisible at first paint (`visibility: hidden` until the user has loaded) and fixed, so
                        it takes no room in the page: a browser render draws it just after hydration, as a
                        transition. It has long appeared by the time the user is in and it is shown. */}
                    <DeferredMount when="hydrated">
                        <Header ticker={stock?.ticker} exchange={stock?.exchangesymbol} stock={stock} scrolledDown={scrolledDown} />
                    </DeferredMount>

                    <main className={classNames(styles[user.email && "user"], stock && styles.mainStockPage, stock && page === "stock-report" && styles.flowsWithPage)}>
                        {
                            // @ts-ignore
                            <StableErrorBoundary fallbackRender={ErrorFallback} onError={logError}>
                                {pageBody}
                            </StableErrorBoundary>
                        }
                    </main>

                    {/* Data pages only (src/lib/lockedPages.ts). Both sit after <main>, which is
                    a fixed viewport-height box, so neither can squeeze a full-height grid:
                    the explainer is reached by scrolling the root, the panel is fixed. */}
                    {/* Below the fold (after the viewport-high <main>), so a browser render draws
                    them when idle or near the viewport; crawlers get them in the server HTML. */}
                    {/* Visitors only, from the page's props: the browser hydrates the same rows. */}
                    {shortListView && <ShortListBlock list={shortList} view={shortListView} />}
                    <DeferredMount when="idle">
                        <AboutThisData stock={stock} />
                    </DeferredMount>
                    <LockedValuePanel stock={stock} />
                    {/* Same placement for the overview's key-data sentence and the site
                    footer: server-rendered text and links, below the fixed-height page. */}
                    <DeferredMount when="idle">{belowPage}</DeferredMount>

                    <Drawer
                        anchor={anchorPosition}
                        id={id}
                        open={settingsModal}
                        onClose={handleClose}
                        slotProps={{
                            paper: {
                                sx: {
                                    paddingTop: "env(safe-area-inset-top)",
                                    backgroundColor: "transparent",
                                    display: "flex",
                                    alignItems: "normal",
                                    justifyContent: "flex-start",

                                    "& .MuiSelect-icon": {
                                        color: theme === "dark" && textColor,
                                    },
                                },
                            },
                        }}
                    >
                        <Card className={styles.drawerContent}>
                            <Box>
                                <Typography variant="h2" sx={{ mb: 2 }}>
                                    Tickertape Settings
                                </Typography>
                                <TickertapeSettings isModal />
                            </Box>
                            {user.subscription_plan !== "ORTEX Visitor" ? (
                                <Box sx={{ alignSelf: "center", marginTop: "auto", display: "flex", justifyContent: "center" }}>
                                    <Button onClick={goToSettings} variant="text">
                                        All settings
                                    </Button>
                                </Box>
                            ) : null}
                        </Card>
                    </Drawer>
                </div>
            </div>
        </DeferHiddenBlocksContext.Provider>
    );
};

export default RootLayout;
